import { useSearchParams } from "react-router-dom";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { useBookingConfirmationRedirect } from "@/hooks/useBookingConfirmationRedirect";
import { useLang } from "@/i18n/LangContext";
import { findCertificateBySlug } from "@/data/certificateSlugs";
import bookingHeroImg from "@/assets/booking-hero.jpg";
import { PageMeta } from "@/components/site/PageMeta";

const IFRAME_URL = "https://intygslakaren.hlthy.se/booking";

const Boka = () => {
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const intygParam = searchParams.get("intyg");
  const slug = intygParam ? findCertificateBySlug(intygParam, lang) ?? intygParam : null;
  useBookingConfirmationRedirect({ slug });


  const title =
    lang === "sv"
      ? "Boka tid läkarintyg | Intygsläkaren"
      : "Book appointment | Intygsläkaren";
  const desc =
    lang === "sv"
      ? "Boka tid online för läkarintyg hos Intygsläkaren i Göteborg. Snabba tider, tydliga priser och professionell vård."
      : "Book an appointment online for a medical certificate at Intygsläkaren in Gothenburg. Fast slots, clear pricing and professional care.";

  const contactHref = lang === "sv" ? "/kontakt" : "/en/contact";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={title} description={desc} />
      <Header />
      <main>
        {/* Hero */}
        <PageHeroCard imageSrc={bookingHeroImg} imageAlt={lang === "sv" ? "Boka tid" : "Book appointment"} compact>
          <div className="animate-fade-up max-w-2xl">
            <h1 className="text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {t.booking.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t.booking.subtitle}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button asChild size="xl" variant="hero">
                <a href="tel:+46317617700">
                  <Phone className="mr-2 h-4 w-4" />
                  {t.booking.callCta}
                </a>
              </Button>
              <Button asChild size="xl" variant="ghost-outline">
                <a href={contactHref}>
                  {t.booking.contactCta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </PageHeroCard>

        {/* Booking iframe */}
        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="mx-auto max-w-3xl text-center">
              <div>
                <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                  {t.booking.sectionTitle}
                </h2>
              </div>
              <p className="mt-4 text-base text-muted-foreground">
                {t.booking.sectionSubtitle}
              </p>
            </div>

            <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-card shadow-card">
              <iframe
                src={IFRAME_URL}
                title={t.booking.iframeTitle}
                className="h-[1100px] w-full border-0 sm:h-[1000px]"
                loading="lazy"
                allow="payment; clipboard-write"
              />
            </div>
          </div>
        </section>

        {/* Help CTA */}
        <section className="px-2 py-16 sm:px-3 lg:px-4 lg:py-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                {t.booking.helpTitle}
              </h2>
              <p className="mt-3 text-base text-primary-foreground/85">{t.booking.helpText}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild variant="onPrimary" size="xl">
                  <a href="tel:+46317617700">
                    <Phone className="mr-2 h-4 w-4" />
                    {t.cta.secondary}
                  </a>
                </Button>
                <Button asChild variant="ghostOnPrimary" size="xl">
                  <a href={contactHref}>
                    <Mail className="mr-2 h-4 w-4" />
                    {t.booking.contactCta}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: t.booking.title },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Boka;
